import { TextShape } from './text-shape';
import { TextWord, TextwordDescriptor } from './text-word';
import { Container, ITextStyle, TextStyle } from 'pixi.js';
import { RectangleElement } from '../primitive/rectangle-element';
import { TextAnimationConfig } from '../../../../elements/resource/types/shape.type';
import { IncrementController } from '../../../animation/frame/increment/controller/increment.controller';
import { TextWordNew } from './text-word-new';
import { TextLetter } from './text-letter';

export interface TextLineDescriptor {
  words: Array<TextwordDescriptor>;
  width: number;
  wrapped: boolean;
}

export class TextLineNew {
  words: TextWordNew[];

  get textShape() {
    return this.parent;
  }

  style: TextStyle;

  get index() {
    return this.config.index;
  }

  get prev(): TextLineNew {
    return this.textShape._lines[this.index - 1];
  }

  get next(): TextLineNew {
    return this.textShape._lines[this.index + 1];
  }

  get lineHeight() {
    return this.config.lineHeight;
  }

  get actualLineHeight() {
    return this.config.actualLineHeight;
  }

  get width() {
    return this.descriptor.width;
  }

  get wrapped() {
    return this.descriptor.wrapped;
  }

  get letters() {
    return this.words.reduce(
      (acc, word) => [...acc, ...word.letters],
      [],
    ) as TextLetter[];
  }
  container: Container;

  constructor(
    private readonly parent: TextShape,
    private readonly descriptor: {
      words: { text: string; width: number }[];
      wrapped: boolean;
      width: number;
    },
    private readonly config: {
      index: number;
      maxLineWidth: number;
      lineHeight: number;
      actualLineHeight: number;
      letterIndexOffset: number;
      style: ITextStyle;
      width: number;
    },
  ) {
    // This override is not needed
    this.style = new TextStyle({
      ...config.style,
      wordWrap: false,
      wordWrapWidth: Infinity,
    });

    this.container = new Container();
    this.parent.circleContainer.addChild(this.container);
    let leftOffset = 0;
    let rightOffset = 0;

    switch (this.style.align) {
      case 'left':
        rightOffset = config.maxLineWidth - this.width;
        break;
      case 'right':
        leftOffset = config.maxLineWidth - this.width;
        break;
      case 'center':
      default:
        leftOffset = (config.maxLineWidth - this.width) / 2;
        rightOffset = leftOffset;
        break;
    }

    leftOffset = (config.maxLineWidth - this.width) / 2;
    rightOffset = leftOffset;

    // -- //
    // this.container.setTransform(leftOffset, 0);
    let letterIndexOffset = config.letterIndexOffset;

    if (
      this.descriptor.words.length == 1 &&
      this.descriptor.words[0].text == ''
    ) {
      this.words = [
        new TextWordNew(
          this,
          {
            text: '',
            width: 0,
          },
          {
            index: 0,
            leftOffset: leftOffset,
            rightOffset,
            lineHeight: this.lineHeight,
            lineYOffset: this.textShape.grossLineHeight * this.index,
            lineWordIndex: 0,
            startCharacterIndex: letterIndexOffset,
          },
        ),
      ];
      return;
    }

    let xOffset = leftOffset;

    this.words = descriptor.words.map((word, index) => {
      // -- // -- // -- // -- //
      const textWord = new TextWordNew(this, word, {
        index,
        xOffset,
        // The first word has the left offset
        leftOffset: index == 0 ? leftOffset : 0,
        // leftOffset,
        // The last word has the right offset
        rightOffset: index == descriptor.words.length - 1 ? rightOffset : 0,
        lineHeight: this.lineHeight,
        lineWordIndex: index,
        startCharacterIndex: letterIndexOffset,
        lineYOffset: this.textShape.grossLineHeight * this.index,
      });
      // -- // -- // -- // -- //

      const { text, width } = word;
      letterIndexOffset += text.length;
      xOffset += width;
      return textWord;
    });
  }

  findLetterByPosition(x: number, strict = false) {
    // for (const word of this.words) {
    //   const res = word.isInMyRegion(x, strict);
    //   if (res) {
    //     return res;
    //   }
    // }
  }

  animate() {
    const length = this.words.length;
    for (let i = 0; i < length; i += 2) {
      // -- // -- //
    }
  }

  show() {
    this.words.map(word => word.show());
    this.showSelectionRectangle();
  }

  showWords() {
    this.words.map(word => word.show());
  }

  hide() {
    this.words.map(word => word.hide());
  }

  hideRectangle() {
    this.selectorRectangle?.hide();
    this.words.map(word => word.hide());
  }
  remove() {
    this.selectorRectangle?.remove();
    this.words?.map(word => word.remove());
  }

  getPrevWord(word: TextWordNew) {
    if (word.index == 0) {
      return this.prevLine?.lastWord;
    }
    return this.words[word.index - 1];
  }
  getNextWord(word: TextWordNew): TextWordNew {
    if (word.index == this.words.length - 1) {
      return this.nextLine?.firstWord;
    }
    return this.words[word.index + 1];
  }
  get prevLine() {
    return this.textShape._lines[this.index - 1];
  }

  get nextLine() {
    return this.textShape._lines[this.index + 1];
  }

  findWord(x: number, strict = false): [TextWordNew, number] {
    // -- //
    for (const word of this.words) {
      const res = word.isInMyRegion(x, strict);
      if (res) {
        return res;
      }
    }
  }

  get firstWord() {
    return this.words[0];
  }

  get lastWord() {
    return this.words[this.words.length - 1];
  }

  get cursorHeight() {
    if (this.textShape.lineHeight < 1) {
      return Math.min(this.textShape.baseLineHeight, this.lineHeight);
    }
    return Math.max(this.textShape.baseLineHeight, this.lineHeight);
  }

  scaleXIncrementController: IncrementController;
  scaleYIncrementController: IncrementController;
  translateXIncrementController: IncrementController;
  translateYIncrementController: IncrementController;
  animationState: {
    start: number;
    end: number;
    division: number;
    current: number;
    state: 'idle' | 'in-progress' | 'finished';
    config: TextAnimationConfig;
    // -- // -- // -- // -- //
  };
  startAnimation(
    { start, end }: { start: number; end: number },
    config: TextAnimationConfig,
  ) {
    const division = end - start;
    //
    // -- // -- // -- // -- // -- // -- // -- //
    //
    this.animationState = {
      start,
      end,
      division,
      current: 0,
      state: 'idle',
      config,
    };

    // .. //
    // .. //
    const { scale, float } = config;

    if (scale) {
      // -- //
      this.scaleXIncrementController = new IncrementController(
        scale == 'grow' ? 2 : 0.2,
        1,
        division,
      );
      this.scaleYIncrementController = new IncrementController(
        scale == 'grow' ? 2 : 0.2,
        1,
        division,
      );
      // -- //

      const dx = this.width / 2;
      const dy = this.lineHeight / 2;

      this.translateXIncrementController = new IncrementController(
        -dx,
        0,
        division,
      );
      this.translateYIncrementController = new IncrementController(
        -dy,
        0,
        division,
      );
    }

    if (float) {
      // -- //
    }
  }

  incrementAnimation(increment: number) {
    if (!this.animationState) {
      return true;
    }

    this.animationState.current += increment;
    const { current, start, end, state } = this.animationState;

    if (state == 'finished') {
      return true;
    }
    // -- //
    // if (index == 0) {
    //  console.log('state', this.animationState);

    if (current < start) {
      return;
    }

    if (end < current && state == 'in-progress') {
      return this.endAnimation();
    }

    this.animationState.state = 'in-progress';

    const scale = {
      x: this.scaleXIncrementController.increment(increment),
      y: this.scaleYIncrementController.increment(increment),
    };

    const translate = {
      x: this.translateXIncrementController.increment(increment),
      y: this.translateYIncrementController.increment(increment),
    };

    this.container.setTransform(translate.x, translate.y, scale.x, scale.y);
  }

  endAnimation() {
    if (!this.animationState) {
      return;
    }
    if (this.animationState.state == 'finished') {
      return;
    }

    console.log('finish animation');
    this.animationState.state = 'finished';

    // if (this.floatIncrementController) {
    //   this.container.setTransform(this.containerX, this.containerY);
    // }

    // this.container.alpha = 1;
  }

  selectorRectangle: RectangleElement;
  showSelectionRectangle(
    startWord?: TextWord,
    startIndex?: number,
    endWord?: TextWord,
    endIndex?: number,
  ) {
    // -- // -- // -- //
    // let start: number;
    const start = startWord
      ? startWord.getXByIndex(startIndex)
      : this.firstWord.xOffset;

    const end = endWord ? endWord.getXByIndex(endIndex) : this.lastWord.end;

    /* console.log({
      start,
      end,
      endWord: !!endWord,
      lineIndex: this.lineIndex,
    }); */

    this.selectorRectangle?.remove();
    this.selectorRectangle = new RectangleElement(
      this.textShape,
      this.textShape.container,
      {
        position: {
          x: start < end ? start : end,
          y: this.textShape.grossLineHeight * this.index,
        },
        width: Math.abs(end - start),
        height: this.textShape.grossLineHeight,
        fill: '#ff0000',
        opacity: 0.2,
      },
    );
  }

  removeSelectorRectangle() {
    this.selectorRectangle?.remove();
  }
}
